/**
 * Document Maître d'Adhésion FTPE Outremer
 * Design Ultra-Professionnel Premium Or et Vert
 * Cadre juridique et institutionnel de l&apos;adhésion syndicale
 */
import React, { useState, useRef } from 'react';
import { Shield, Scale, CheckCircle, Award, Sparkles, ChevronRight, Star, Crown, Handshake, Heart, Target, Download, PenTool, BookOpen, Scroll, Gem } from 'lucide-react';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

// Logo FTPE Outremer
const FTPE_LOGO = "https://customer-assets.emergentagent.com/job_80d9c916-7281-4428-9c84-a6e0cd256c92/artifacts/ojac2aim_logo%20ftpe%20outremer%20dans%20fond%20blanc.png";

// Configuration des couleurs Premium
const _COLORS = {
  gold: '#C9A24D',
  goldLight: '#E8D5A3',
  goldDark: '#9A7B3A',
  green: '#0E6B4E',
  greenLight: '#E6F2EC',
  greenDark: '#0A5740',
  dark: '#0C0C0C',
  white: '#FFFFFF'
};

// Sections du document
const DOCUMENT_SECTIONS = [
  {
    id: 'preambule',
    title: 'Préambule',
    icon: BookOpen,
    content: `Le présent document constitue le **document maître d&apos;adhésion** à la FTPE Outremer. Il définit le cadre juridique, institutionnel et éthique applicable à toute entreprise souhaitant devenir membre du syndicat patronal des Très Petites Entreprises ultramarines.`
  },
  {
    id: 'cadre-juridique',
    title: 'Cadre juridique de référence',
    icon: Scale,
    content: `L'adhésion est régie par les documents suivants, réputés lus et acceptés :`,
    items: [
      'Statuts de la FTPE Outremer (12 juin 2025)',
      'Règlement intérieur (12 juin 2025)',
      'Charte d\'éthique FTPE Outremer (12 juin 2025)'
    ]
  },
  {
    id: 'objet',
    title: 'Objet de l\'adhésion',
    icon: Target,
    content: `L'adhésion permet à l&apos;employeur d'intégrer un **syndicat patronal ultramarin indépendant et non lucratif**, œuvrant pour la représentation, la défense et l'accompagnement responsable des TPE dans les territoires d'Outre-mer.`
  },
  {
    id: 'role-limites',
    title: 'Rôle et limites de la FTPE Outremer',
    icon: Shield,
    content: `La FTPE Outremer agit exclusivement en qualité de syndicat patronal. Elle peut accompagner, faciliter et orienter ses adhérents vers un **écosystème de partenaires institutionnels, techniques et solidaires**, dans le respect strict de son rôle syndical.`,
    notice: `Elle ne commercialise aucun service, n'exerce aucune activité réglementée et n'assume aucune responsabilité opérationnelle des partenaires.`
  },
  {
    id: 'dispositifs',
    title: 'Accès aux dispositifs partenaires',
    icon: Handshake,
    content: `L'adhésion à la FTPE Outremer constitue le **cadre institutionnel préalable** pour l'accès aux dispositifs solidaires COPPAM et aux services de l'écosystème O'SCOP, selon les conditions propres à chaque dispositif.`
  },
  {
    id: 'engagement',
    title: 'Engagement de l\'adhérent',
    icon: Heart,
    content: `L'adhérent s'engage à :`,
    items: [
      'Respecter les statuts, le règlement intérieur et la charte d\'éthique',
      'S\'acquitter de la cotisation annuelle',
      'Adopter un comportement loyal, responsable et éthique',
      'Respecter la confidentialité des informations internes',
      'Participer activement à la vie syndicale'
    ]
  }
];

// Composant décoratif Premium
const PremiumBorder = ({ className }) => (
  <div className={cn("absolute inset-0 pointer-events-none", className)}>
    <div className="absolute top-0 left-0 w-24 h-24 border-t-4 border-l-4 border-[#C9A24D] rounded-tl-xl" />
    <div className="absolute top-0 right-0 w-24 h-24 border-t-4 border-r-4 border-[#C9A24D] rounded-tr-xl" />
    <div className="absolute bottom-0 left-0 w-24 h-24 border-b-4 border-l-4 border-[#C9A24D] rounded-bl-xl" />
    <div className="absolute bottom-0 right-0 w-24 h-24 border-b-4 border-r-4 border-[#C9A24D] rounded-br-xl" />
  </div>
);

// Composant Gold Divider
const GoldDivider = () => (
  <div className="flex items-center justify-center gap-3 my-8">
    <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C9A24D]" />
    <Gem className="w-5 h-5 text-[#C9A24D]" />
    <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C9A24D]" />
  </div>
);

// Composant Section Card
const SectionCard = ({ section, index }) => {
  const Icon = section.icon;
  
  return (
    <div className="group relative">
      {/* Numéro de section */}
      <div className="absolute -left-4 top-0 w-8 h-8 bg-gradient-to-br from-[#C9A24D] to-[#9A7B3A] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg z-10">
        {String(index + 1).padStart(2, '0')}
      </div>
      
      <div className="ml-6 bg-white rounded-xl border border-slate-200 overflow-hidden transition-all duration-300 group-hover:shadow-xl group-hover:border-[#C9A24D]/30">
        {/* Header de section */}
        <div className="bg-gradient-to-r from-[#E6F2EC] to-white px-6 py-4 border-l-4 border-[#C9A24D] flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-[#0E6B4E] to-[#0A5740] rounded-xl shadow-md">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-[#0E6B4E]">{section.title}</h3>
        </div>
        
        {/* Contenu */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-slate-700 leading-relaxed" 
             dangerouslySetInnerHTML={{ 
               __html: sanitizeHtml(section.content.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#0E6B4E] font-semibold">$1</strong>'))
             }} 
          />
          
          {section.items && (
            <ul className="space-y-2 ml-4">
              {section.items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C9A24D] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">{item}</span>
                </li>
              ))}
            </ul>
          )}
          
          {section.notice && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm italic">{section.notice}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant Principal
export default function DocumentMaitreAdhesionFTPE({ 
  onAccept, 
  onDownload,
  showSignatureButton = true,
  cotisation = 200 
}) {
  const [hasRead, setHasRead] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const documentRef = useRef(null);

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 100;
    if (isAtBottom && !isScrolledToEnd) {
      setIsScrolledToEnd(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      
      {/* =============================== */}
      {/* HEADER PREMIUM */}
      {/* =============================== */}
      <header className="relative overflow-hidden">
        {/* Background avec dégradé premium */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0C0C0C] via-[#0E6B4E] to-[#0A5740]" />
        
        {/* Motif décoratif */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C9A24D] rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#C9A24D] rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>
        
        {/* Lignes dorées décoratives */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C9A24D] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C9A24D] to-transparent" />
        
        <div className="relative z-10 container mx-auto px-6 py-16 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-[#C9A24D] rounded-2xl blur-xl opacity-30 transform scale-110" />
              <div className="relative bg-white rounded-2xl p-4 shadow-2xl border-2 border-[#C9A24D]/30">
                <img 
                  src={FTPE_LOGO} 
                  alt="FTPE Outremer" 
                  className="w-24 h-24 object-contain"
                />
              </div>
              {/* Badge Premium */}
              <div className="absolute -top-3 -right-3 bg-gradient-to-br from-[#C9A24D] to-[#9A7B3A] rounded-full p-2 shadow-lg">
                <Crown className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
          
          {/* Titre principal */}
          <div className="space-y-4">
            <Badge className="bg-[#C9A24D]/20 text-[#C9A24D] border border-[#C9A24D]/30 px-4 py-1 text-sm font-medium">
              <Sparkles className="w-4 h-4 mr-2" />
              Document Officiel
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Document maître d&apos;adhésion
            </h1>
            
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              FTPE Outremer – Syndicat patronal des Très Petites Entreprises ultramarines
            </p>
            
            {/* Valeurs */}
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              {['Indépendance', 'Responsabilité', 'Solidarité ESS'].map((value, idx) => (
                <div key={idx} className="flex items-center gap-2 text-[#C9A24D]">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="text-sm font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* =============================== */}
      {/* CONTENU PRINCIPAL */}
      {/* =============================== */}
      <main className="container mx-auto px-4 py-12">
        <div 
          ref={documentRef}
          onScroll={handleScroll}
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden relative"
        >
          {/* Bordure Premium */}
          <PremiumBorder />
          
          <div className="p-8 md:p-12">
            
            {/* En-tête du document */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E6F2EC] to-white rounded-full border border-[#0E6B4E]/20 mb-4">
                <Scroll className="w-5 h-5 text-[#0E6B4E]" />
                <span className="text-[#0E6B4E] font-medium">Cadre juridique et institutionnel</span>
              </div>
              <p className="text-slate-500 text-sm">
                Version du 12 juin 2025 • Document de référence
              </p>
            </div>
            
            <GoldDivider />
            
            {/* Sections du document */}
            <div className="space-y-8">
              {DOCUMENT_SECTIONS.map((section, index) => (
                <SectionCard key={section.id} section={section} index={index} />
              ))}
            </div>
            
            <GoldDivider />
            
            {/* =============================== */}
            {/* SECTION CONDITIONS FINANCIÈRES */}
            {/* =============================== */}
            <div className="my-12">
              <div className="relative bg-gradient-to-br from-[#0E6B4E] to-[#0A5740] rounded-2xl p-8 overflow-hidden">
                {/* Décoration */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#C9A24D] rounded-full blur-3xl opacity-10 transform translate-x-1/2 -translate-y-1/2" />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-white/10 rounded-xl">
                      <Award className="w-6 h-6 text-[#C9A24D]" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Conditions financières</h3>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <p className="text-white/80 mb-4">Cotisation annuelle d&apos;adhésion :</p>
                    
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-[#C9A24D]">{cotisation}</span>
                      <span className="text-2xl text-[#C9A24D]">€</span>
                      <span className="text-white/60 ml-2">/ an</span>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-white/70 text-sm">
                        Cette cotisation est indépendante de tout dispositif ou service partenaire 
                        et ne constitue pas une facture commerciale.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* =============================== */}
            {/* SECTION SIGNATURE */}
            {/* =============================== */}
            {showSignatureButton && (
              <div className="mt-12">
                <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 overflow-hidden">
                  {/* Effet brillant */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#C9A24D]/10 to-transparent animate-pulse" />
                  
                  {/* Bordure dorée */}
                  <div className="absolute inset-0 rounded-2xl border-2 border-[#C9A24D]/30" />
                  
                  <div className="relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A24D]/20 rounded-full mb-6">
                      <PenTool className="w-5 h-5 text-[#C9A24D]" />
                      <span className="text-[#C9A24D] font-semibold">Validation et signature</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-4">
                      Prêt à rejoindre la FTPE Outremer ?
                    </h3>
                    
                    <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                      En poursuivant, vous confirmez avoir pris connaissance du présent document 
                      maître d&apos;adhésion et accepter l&apos;ensemble de ses dispositions.
                    </p>
                    
                    {/* Checkbox confirmation */}
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <input
                        type="checkbox"
                        id="accept-terms"
                        checked={hasRead}
                        onChange={(e) => setHasRead(e.target.checked)}
                        className="w-5 h-5 rounded border-2 border-[#C9A24D] text-[#C9A24D] focus:ring-[#C9A24D] focus:ring-offset-slate-900"
                      />
                      <label htmlFor="accept-terms" className="text-slate-300 text-sm cursor-pointer">
                        J&apos;ai lu et j&apos;accepte les termes du document maître d&apos;adhésion
                      </label>
                    </div>
                    
                    {/* Boutons d&apos;action */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      {onDownload && (
                        <Button
                          variant="outline"
                          onClick={onDownload}
                          className="border-[#C9A24D]/50 text-[#C9A24D] hover:bg-[#C9A24D]/10"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Télécharger le document
                        </Button>
                      )}
                      
                      <Button
                        onClick={onAccept}
                        disabled={!hasRead}
                        className={cn(
                          "relative overflow-hidden px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-300",
                          hasRead 
                            ? "bg-gradient-to-r from-[#C9A24D] via-[#E8D5A3] to-[#C9A24D] text-slate-900 hover:shadow-2xl hover:shadow-[#C9A24D]/30 hover:scale-105" 
                            : "bg-slate-700 text-slate-400 cursor-not-allowed"
                        )}
                      >
                        {hasRead && (
                          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                        )}
                        <span className="relative flex items-center gap-2">
                          <PenTool className="w-5 h-5" />
                          J&apos;ai lu et je signe électroniquement
                          <ChevronRight className="w-5 h-5" />
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
          </div>
        </div>
      </main>

      {/* =============================== */}
      {/* FOOTER */}
      {/* =============================== */}
      <footer className="bg-[#0C0C0C] py-8 mt-12">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src={FTPE_LOGO} alt="FTPE" className="w-10 h-10 object-contain bg-white rounded-lg p-1" />
            <span className="text-white font-semibold">FTPE Outremer</span>
          </div>
          <p className="text-slate-400 text-sm">
            Syndicat patronal ultramarin
          </p>
          <div className="flex items-center justify-center gap-6 mt-4 text-[#C9A24D] text-sm">
            <span>Indépendance</span>
            <span className="w-1 h-1 bg-[#C9A24D] rounded-full" />
            <span>Responsabilité</span>
            <span className="w-1 h-1 bg-[#C9A24D] rounded-full" />
            <span>Solidarité ESS</span>
          </div>
        </div>
      </footer>
      
      {/* Animation CSS pour l'effet shimmer */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
}
